import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';

@Component({
  selector: 'app-about-us',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, FooterComponent],
  templateUrl: './about-us.component.html',
  styleUrls: ['./about-us.component.css'],
})
export class AboutUsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('pageContent') pageContent!: ElementRef;

  activeSection: string = '';
  private observer!: IntersectionObserver;
  private isScrolling = false;

  sections = [
    {
      id: 'our-story',
      title: 'Our Story',
      content: `
        <p><strong>Markado</strong> was founded in 2025 by two university students from Tetovo, North Macedonia, who recognized the need for a modern, user-friendly marketplace platform in North Macedonia. What began as a computer science project during our studies has evolved into a comprehensive web application designed to connect buyers and sellers across the region.</p>

        <p>As students at the South East European University, we observed how people in our community and throughout North Macedonia were buying and selling goods online. While other platforms had established the concept of online classifieds in our region, we saw opportunities to improve the user experience with modern web technologies and better design.</p>

        <p>During late-night coding sessions in local cafés, we built the first version of Markado with a clear goal: create a platform where sellers can easily showcase their products with attractive listings, and buyers can browse and connect with sellers effortlessly. We wanted to combine the familiar classified ads model that our region knows and trusts with a fresh, intuitive interface.</p>

        <p>Our journey from university project to functioning marketplace reflects our commitment to understanding the unique needs of each and every user. We built Markado not just as a business venture, but as a way to contribute to the growing digital economy in our region while providing a platform that truly serves our community.</p>
      `,
    },
    {
      id: 'mission-vision',
      title: 'Our Mission & Vision',
      content: `
        <h3>Our Mission</h3>
        <p>To provide a modern, accessible online marketplace where sellers throughout North Macedonia can showcase their products effectively, and buyers can easily discover and connect with them. We aim to bridge the gap between traditional classified advertising and modern e-commerce, creating a platform that feels familiar yet provides enhanced functionality and user experience.</p>

        <h3>Our Vision</h3>
        <p>We envision Markado becoming the go-to marketplace platform for North Macedonia, where small businesses, individual sellers, and entrepreneurs can reach customers across the region. Our goal is to foster local commerce while embracing modern web technologies, helping to grow the digital economy in our part of the world.</p>

        <p>We believe that every seller, whether they're running a small business from their home or managing a growing enterprise, deserves access to professional-quality tools for showcasing their products. Similarly, every buyer should have access to a clean, fast, and reliable platform for discovering products and connecting with sellers.</p>

        <p>Our long-term vision extends beyond just facilitating connections between buyers and sellers. We want to support the entrepreneurial spirit that we see throughout North Macedonia, providing a platform where local businesses can thrive, students can earn extra income, and families can find quality products at fair prices from trusted sellers in their region.</p>
      `,
    },
    {
      id: 'values',
      title: 'Our Values',
      content: `
        <p>As a company founded by students from North Macedonia, our values reflect our cultural background, our commitment to community, and our belief in the potential of technology to improve people's lives in meaningful ways.</p>

        <div class="values-grid">
          <div class="value-card">
            <div class="value-icon-container">
              <svg viewBox="0 0 24 24" class="value-icon">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
            <h3>Trust & Reliability</h3>
            <p>We understand that trust is essential in online marketplaces, especially when buyers and sellers are connecting directly. We build features and policies that promote honest communication and reliable transactions.</p>
          </div>

          <div class="value-card">
            <div class="value-icon-container">
              <svg viewBox="0 0 24 24" class="value-icon">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                <line x1="9" y1="9" x2="9.01" y2="9"></line>
                <line x1="15" y1="9" x2="15.01" y2="9"></line>
              </svg>
            </div>
            <h3>Simple & Intuitive Design</h3>
            <p>We believe technology should be accessible to everyone, regardless of their technical expertise. Our platform is designed to be straightforward and easy to use for both sellers creating listings and buyers browsing products.</p>
          </div>

          <div class="value-card">
            <div class="value-icon-container">
              <svg viewBox="0 0 24 24" class="value-icon">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
              </svg>
            </div>
            <h3>Regional Focus</h3>
            <p>We have committed to understanding the unique needs, preferences, and business practices of our region. We build with local users in mind while embracing global web standards.</p>
          </div>

          <div class="value-card">
            <div class="value-icon-container">
              <svg viewBox="0 0 24 24" class="value-icon">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
              </svg>
            </div>
            <h3>Accessibility</h3>
            <p>We want Markado to be accessible to everyone - from tech-savvy entrepreneurs to people who are just getting started with online selling. Our platform works across devices and doesn't require advanced technical knowledge.</p>
          </div>

          <div class="value-card">
            <div class="value-icon-container">
              <svg viewBox="0 0 24 24" class="value-icon">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h3>Community Support</h3>
            <p>We're not just building a platform; we're supporting a community of sellers and buyers. We believe in fostering connections that go beyond individual transactions and help strengthen local commerce.</p>
          </div>

          <div class="value-card">
            <div class="value-icon-container">
              <svg viewBox="0 0 24 24" class="value-icon">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <h3>Continuous Improvement</h3>
            <p>As students and lifelong learners, we're constantly working to improve our platform based on user feedback, new technologies, and changing needs in the marketplace. We're never satisfied with "good enough."</p>
          </div>
        </div>
      `,
    },
    {
      id: 'how-it-works',
      title: 'How Markado Works',
      content: `
        <p>Markado operates on a simple, proven model: sellers create attractive listings to showcase their products, and interested buyers can browse these listings and contact sellers directly. Our platform combines the familiarity of traditional classified ads with modern web design and functionality.</p>

        <div class="process-steps">
          <div class="process-step">
            <div class="step-content">
              <h3>Create Your Profile</h3>
              <p>Sign up for free and create your profile. That way you can enjoy each and every single feature of Markado.</p>
            </div>
          </div>

          <div class="process-step">
            <div class="step-content">
              <h3>Post Your Products</h3>
              <p>Create detailed listings with high-quality photos, comprehensive descriptions, and competitive prices. Organize your products by category, and include all the information buyers need to make informed decisions.</p>
            </div>
          </div>

          <div class="process-step">
            <div class="step-content">
              <h3>Connect with Buyers</h3>
              <p>When buyers are interested in your products, they can contact you directly through our secure messaging system, by phone, or through other contact methods you've provided.</p>
            </div>
          </div>

          <div class="process-step">
            <div class="step-content">
              <h3>Complete Your Sale</h3>
              <p>Arrange payment, delivery, or pickup directly with your buyers. Whether you prefer cash transactions, bank transfers, or in-person meetings, you have the flexibility to conduct business in the way that works best for you.</p>
            </div>
          </div>
        </div>
      `,
    },
    {
      id: 'global-presence',
      title: 'Our Base & Growth',
      content: `
        <p>Markado was born in Tetovo, North Macedonia, and we remain proudly rooted in Tetovo. As students who understand the local market and culture, we're building a platform that truly serves the needs of our region while leveraging modern web technologies and best practices.</p>

        <p>Operating from Tetovo gives us a unique perspective on the needs of our users. We understand the importance of personal connections in business, the preference for direct communication between buyers and sellers, and the value of building trust within local communities. This understanding shapes every decision we make about our platform.</p>

        <div class="office-locations">
          <div class="office-card">
            <h3>Home Base</h3>
            <p class="office-location">Tetovo, North Macedonia</p>
            <p>Founded and operated from Tetovo, where we continue to develop our platform.</p>
          </div>

          <div class="office-card">
            <h3>Target Region</h3>
            <p class="office-location">Balkan Region</p>
            <p>While we started in North Macedonia, we're expanding throughout the Balkans, adapting our platform to serve users across the region with localized features and support.</p>
          </div>

          <div class="office-card">
            <h3>Development Team</h3>
            <p class="office-location">Student Entrepreneurs</p>
            <p>As current university students, we bring fresh perspectives and modern technical skills to traditional marketplace concepts, creating something that bridges generational and technological gaps.</p>
          </div>

          <div class="office-card">
            <h3>Growth Strategy</h3>
            <p class="office-location">Community-Driven</p>
            <p>We're growing organically through word-of-mouth recommendations and community engagement, focusing on building a loyal user base rather than rapid expansion at the expense of quality.</p>
          </div>
        </div>

        <p>Being based in North Macedonia allows us to serve a market that often gets overlooked by larger international platforms. We understand the local languages, customs, and business practices that make our region unique. This local knowledge, combined with our technical skills and entrepreneurial drive, positions us to create something truly valuable for our community.</p>

        <p>As we grow, we remain committed to our roots while embracing the opportunities that technology provides to connect people and facilitate commerce across borders. We're excited about the future of Markado and the role we can play in supporting the digital economy of North Macedonia.</p>
      `,
    },
  ];

  constructor() {}

  ngOnInit(): void {
    this.activeSection = this.sections[0]?.id || '';
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.pageContent && this.pageContent.nativeElement) {
        this.pageContent.nativeElement.classList.add('loaded');
      }
      this.setupIntersectionObserver();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {}

  private setupIntersectionObserver(): void {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3,
    };

    this.observer = new IntersectionObserver((entries) => {
      if (this.isScrolling) return;

      const visibleEntries = entries.filter((entry) => entry.isIntersecting);

      if (visibleEntries.length > 0) {
        const firstVisible = visibleEntries[0];
        const sectionId = firstVisible.target.id;

        if (sectionId && this.activeSection !== sectionId) {
          this.activeSection = sectionId;
        }
      }
    }, options);

    this.sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        this.observer.observe(element);
      }
    });
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      this.isScrolling = true;
      this.activeSection = sectionId;

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      setTimeout(() => {
        this.isScrolling = false;
      }, 1000);
    }
  }

  isActive(sectionId: string): boolean {
    return this.activeSection === sectionId;
  }

  trackBySection(index: number, section: any): string {
    return section.id;
  }
}
